import { useMemo } from "react";
import { NeighborhoodMapProperty } from "@cma/presentation-generation-logic";
import { MapMarker } from "./MapMarker";
import styled from "@emotion/styled";
import { appConfig } from "@shared/utils/appConfig";

type GoogleMapPrintProps = {
  properties: NeighborhoodMapProperty[];
};

// Logical image dimensions (scale=2 doubles the actual pixel output for sharpness)
const IMG_WIDTH = 640;
const IMG_HEIGHT = 400;
const SCALE = 2;
const PADDING_PX = 60; // keep markers away from edges

const MapRoot = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${IMG_WIDTH} / ${IMG_HEIGHT};
  overflow: hidden;
`;

const MapImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
`;

function latLngToWorld(lat: number, lng: number, zoom: number) {
  const worldSize = 256 * Math.pow(2, zoom);
  const x = ((lng + 180) / 360) * worldSize;
  const latRad = (lat * Math.PI) / 180;
  const y =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) *
    worldSize;
  return { x, y };
}

function calcZoom(
  minLat: number,
  maxLat: number,
  minLng: number,
  maxLng: number,
): number {
  // Single point — default to a comfortable street-level zoom
  if (maxLat - minLat < 0.001 && maxLng - minLng < 0.001) {
    return 15;
  }
  const effectiveWidth = IMG_WIDTH - PADDING_PX * 2;
  const effectiveHeight = IMG_HEIGHT - PADDING_PX * 2;
  for (let z = 16; z >= 1; z--) {
    const sw = latLngToWorld(minLat, minLng, z);
    const ne = latLngToWorld(maxLat, maxLng, z);
    if (
      Math.abs(ne.x - sw.x) <= effectiveWidth &&
      Math.abs(ne.y - sw.y) <= effectiveHeight
    ) {
      return z;
    }
  }
  return 1;
}

type MarkerEntry = {
  properties: NeighborhoodMapProperty[];
  type: string;
  /** percentage position from top-left of the image container */
  xPct: number;
  yPct: number;
};

export const GoogleMapPrint = ({ properties }: GoogleMapPrintProps) => {
  const result = useMemo(() => {
    if (!properties || properties.length === 0) return null;

    const apiKey = appConfig.GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn("Google Maps API key not found");
      return null;
    }

    // Bounds
    let minLat = Infinity,
      maxLat = -Infinity,
      minLng = Infinity,
      maxLng = -Infinity;
    properties.forEach(({ lat, lng }) => {
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    });

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    const zoom = calcZoom(minLat, maxLat, minLng, maxLng);

    // Base map — no markers in the URL; markers are rendered as HTML on top
    const mapUrl =
      `https://maps.googleapis.com/maps/api/staticmap` +
      `?center=${centerLat},${centerLng}` +
      `&zoom=${zoom}` +
      `&size=${IMG_WIDTH}x${IMG_HEIGHT}` +
      `&scale=${SCALE}` +
      `&style=feature:poi|element:all|visibility:off` +
      `&style=feature:transit|element:all|visibility:off` +
      `&key=${apiKey}`;

    // Group properties that share the same location
    const groups: Record<string, NeighborhoodMapProperty[]> = {};
    properties.forEach((p) => {
      const key = `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`;
      groups[key] = (groups[key] ?? []).concat([p]);
    });

    const centerWorld = latLngToWorld(centerLat, centerLng, zoom);

    const markers: MarkerEntry[] = Object.values(groups).map((groupProps) => {
      const p1 = groupProps[0];
      const world = latLngToWorld(p1.lat, p1.lng, zoom);
      const xPct =
        ((IMG_WIDTH / 2 + (world.x - centerWorld.x)) / IMG_WIDTH) * 100;
      const yPct =
        ((IMG_HEIGHT / 2 + (world.y - centerWorld.y)) / IMG_HEIGHT) * 100;
      return {
        properties: groupProps,
        type: groupProps.find((x) => x.type === "subject")
          ? "subject"
          : p1.type,
        xPct,
        yPct,
      };
    });

    return { mapUrl, markers };
  }, [properties]);

  if (!result) return null;

  return (
    <MapRoot>
      <MapImage src={result.mapUrl} alt="Neighborhood Map" />
      {result.markers.map((m, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${m.xPct}%`,
            top: `${m.yPct}%`,
            pointerEvents: "none",
          }}
        >
          <MapMarker
            lat={0}
            lng={0}
            propertyType={m.type}
            numProperties={m.properties.length}
          />
        </div>
      ))}
    </MapRoot>
  );
};
